// src/pages/checkout/checkout.component.jsx
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { selectCartItems, selectCartTotal } from '../../redux/cart/cart.selectors';
import { clearCart } from '../../redux/cart/cart.actions';
import { createOrder } from '../../redux/order/order.actions';
import CheckoutItem from '../../components/checkout-item/checkout-item.component';
import './checkout.styles.scss';

const CheckoutPage = ({ cartItems, total, clearCart, createOrder }) => {
  const [step, setStep] = useState(1);
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });

  const whatsappLink = "https://wa.me/923224314712";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.fullName || !formData.phone || !formData.address || !formData.city) {
      alert('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      // ✅ Clean data - remove undefined values
      const orderData = {
        customerName: formData.fullName || 'N/A',
        phone: formData.phone || 'N/A',
        email: formData.email || '',
        address: formData.address || 'N/A',
        city: formData.city || 'N/A',
        postalCode: formData.postalCode || '',
        notes: formData.notes || '',
        items: cartItems.map(item => ({
          id: item.id || 'N/A',
          name: item.name || 'Product',
          price: item.price || 0,
          quantity: item.quantity || 1,
          image: item.imageUrl || ''
        })),
        totalAmount: total || 0,
        itemCount: cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0,
        orderType: 'direct',
        paymentMethod: 'cash',
        status: 'pending'
      };

      console.log('📦 Order Data:', orderData); // Debug log

      const result = await createOrder(orderData);

      if (result.success) {
        setOrderNumber(result.orderNumber);
        setStep(3);
        
        const message = `🦁 CRISPY HAIR SOLUTION - NEW ORDER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 Order #${result.orderNumber}
─────────────────────────────────
👤 Customer: ${formData.fullName}
📱 Phone: ${formData.phone}
📍 Address: ${formData.address}
🏙️ City: ${formData.city}

🛒 Items (${cartItems.reduce((sum, item) => sum + item.quantity, 0)} items):
${cartItems.map(item => `  • ${item.name} x${item.quantity} = Rs. ${(item.price * item.quantity).toLocaleString()}`).join('\n')}

💰 Total: Rs. ${total.toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Please confirm this order.`;

        window.open(`${whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
        setLoading(false);
        
        setTimeout(() => {
          clearCart();
        }, 3000);
      } else {
        alert('Error placing order: ' + result.error);
        setLoading(false);
      }
    } catch (error) {
      console.error('Order Error:', error);
      alert('Error placing order: ' + error.message);
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && step !== 3) {
    return (
      <div className="checkout-page empty-cart">
        <h2>🛒 Your Cart is Empty</h2>
        <p>Add some products to your cart and come back!</p>
        <a href="/" className="continue-shopping">Continue Shopping</a>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="checkout-page success-page">
        <div className="success-container">
          <div className="success-icon">✅</div>
          <h2>Order Placed Successfully!</h2>
          <p className="order-number">Order #: <strong>{orderNumber}</strong></p>
          <p>Thank you for your order! We will confirm it shortly.</p>
          <div className="success-actions">
            <a href="/" className="continue-btn">Continue Shopping</a>
            <a href="/track-order" className="track-btn">Track Order</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>Checkout</h1>

        <div className="checkout-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Review Cart</span>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Shipping Details</span>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Confirmation</span>
          </div>
        </div>

        {step === 1 && (
          <div className="checkout-step">
            <div className="checkout-items">
              <h2>Review Your Order</h2>
              <div className="items-header">
                <span>Product</span>
                <span>Qty</span>
                <span>Price</span>
                <span>Total</span>
              </div>
              {cartItems.map(cartItem => (
                <CheckoutItem key={cartItem.id} cartItem={cartItem} />
              ))}
              <div className="order-total">
                <div className="total-row grand-total">
                  <span>Total</span>
                  <span>Rs. {total.toLocaleString()}</span>
                </div>
              </div>
              <button className="proceed-btn" onClick={() => setStep(2)}>
                Proceed to Checkout →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="checkout-step">
            <div className="checkout-form">
              <h2>Shipping Details</h2>
              <p>Please fill in your delivery information</p>

              <form onSubmit={handlePlaceOrder}>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Delivery Address *</label>
                  <input
                    type="text"
                    name="address"
                    placeholder="Enter your complete address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      placeholder="Enter your city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      placeholder="Enter postal code"
                      value={formData.postalCode}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Order Notes</label>
                  <textarea
                    name="notes"
                    placeholder="Any special instructions..."
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                <div className="order-summary">
                  <h3>Order Summary</h3>
                  <div className="summary-row">
                    <span>Items ({cartItems.reduce((sum, item) => sum + item.quantity, 0)})</span>
                    <span>Rs. {total.toLocaleString()}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <span>Rs. {total.toLocaleString()}</span>
                  </div>
                </div>

                <div className="form-buttons">
                  <button type="button" className="back-btn" onClick={() => setStep(1)}>
                    ← Back
                  </button>
                  <button type="submit" className="place-order-btn" disabled={loading}>
                    {loading ? 'Placing Order...' : 'Place Order'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  cartItems: selectCartItems,
  total: selectCartTotal
});

const mapDispatchToProps = dispatch => ({
  clearCart: () => dispatch(clearCart()),
  createOrder: (orderData) => dispatch(createOrder(orderData))
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutPage);